import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { Store } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useUpdateStore } from '@/features/stores';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';

import {
  fromUnpaidOrderHold,
  toUnpaidOrderHoldPatch,
  unpaidOrderHoldSchema,
  type UnpaidOrderHoldValues,
} from '../schemas/unpaid-order-hold-schema';

const mapField = (field: string) => field.replace(/^settings\.checkout\./, '');
const KNOWN_FIELDS: ReadonlySet<string> = new Set(['unpaidOrderHoldDays']);

export interface UnpaidOrderHoldCardProps {
  store: Store;
  readOnly: boolean;
}

/**
 * `settings.checkout.unpaidOrderHoldDays`: how long an order placed with a manual method may stay
 * unpaid before the worker cancels it. Lives here because it only matters for manual methods.
 */
export function UnpaidOrderHoldCard({ store, readOnly }: UnpaidOrderHoldCardProps) {
  const { t } = useTranslation(['paymentMethods', 'common']);
  const update = useUpdateStore(store.id, 'paymentMethods:hold.saved');
  const message = useErrorMessage();
  const form = useForm<UnpaidOrderHoldValues>({
    resolver: useI18nResolver(unpaidOrderHoldSchema),
    defaultValues: fromUnpaidOrderHold(store),
  });
  const days = Number(form.watch('unpaidOrderHoldDays'));

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync(toUnpaidOrderHoldPatch(values));
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <FormSection
      id={SECTION_ANCHORS.unpaidOrders}
      tour="settings.payment-methods.unpaid-hold"
      title={t('paymentMethods:hold.title')}
      description={t('paymentMethods:hold.description')}
    >
      <Form {...form}>
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <FormField
            control={form.control}
            name="unpaidOrderHoldDays"
            render={({ field }) => (
              <FormItem className="max-w-xs">
                <FormLabel>{t('paymentMethods:hold.days')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={60}
                    step={1}
                    disabled={readOnly}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {Number.isInteger(days) && days >= 1 && days <= 60
                    ? t('paymentMethods:hold.daysHint', {
                        days,
                        reminder: Math.max(1, Math.floor(days / 2)),
                      })
                    : t('paymentMethods:hold.daysRange')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          {readOnly ? null : (
            <div className="flex justify-end">
              <Button
                type="submit"
                size="sm"
                disabled={update.isPending || !form.formState.isDirty}
              >
                {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </FormSection>
  );
}
